import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLang } from '../i18n/LangContext';
import { ShieldCheck, Clock, CheckCircle2, Server, Mail, ArrowLeft, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BADGE = {
  en_preparacion: ['En preparación', 'bg-amber-500/15 text-amber-300 border-amber-500/30', Clock],
  disponible: ['Disponible', 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', CheckCircle2],
  certificado: ['Certificado', 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', ShieldCheck],
};

export default function TrustCenterPage() {
  const [t, setT] = useState(null);
  const { lang } = useLang();
  useEffect(() => { axios.get(`${API}/api/security/trust`, { params: { lang } }).then((r) => setT(r.data)).catch(() => setT({})); }, [lang]);

  return (
    <div className="min-h-screen mesh-gradient">
      <header className="glass border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="trust-home-link" className="flex items-center gap-2 text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /> NotaryOS</Link>
          <span className="flex items-center gap-2 font-semibold font-['Outfit']"><ShieldCheck className="w-5 h-5 text-emerald-400" /> Centro de Confianza</span>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-14 space-y-12" data-testid="trust-center-page">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-400 mb-3">Seguridad · Privacidad · Cumplimiento</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] tracking-tight">El secreto profesional, protegido en cada capa.</h1>
          <p className="text-slate-400 mt-4 text-sm sm:text-base">Mostramos con transparencia qué controles operan hoy y en qué estado están nuestras certificaciones.</p>
        </div>
        {!t ? <p className="text-slate-500">Cargando…</p> : (
          <>
            <section className="grid md:grid-cols-3 gap-4">
              {(t.certifications || []).map((c) => {
                const [label, cls, Icon] = BADGE[c.status] || BADGE.en_preparacion;
                return (
                  <div key={c.id} className="glass rounded-2xl border border-border p-5" data-testid={`trust-cert-${c.id}`}>
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${cls}`}><Icon className="w-3.5 h-3.5" /> {label}</span>
                    <h3 className="text-lg font-semibold mt-3">{c.name}</h3>
                    <p className="text-sm text-slate-400 mt-2">{c.detail}</p>
                  </div>
                );
              })}
            </section>
            <section>
              <h2 className="text-lg font-semibold mb-4">Controles en operación</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {(t.controls || []).map((g) => (
                  <div key={g.area} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="trust-control-group">
                    <p className="text-sm font-semibold text-emerald-300 mb-3">{g.area}</p>
                    <ul className="space-y-2">{g.items.map((i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{i}</li>)}</ul>
                  </div>
                ))}
              </div>
              {t.live && <p className="text-xs text-slate-500 mt-3" data-testid="trust-live-mfa">Adopción de doble factor en la plataforma: {t.live.mfa_adoption_pct}%</p>}
            </section>
            <section>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Server className="w-5 h-5 text-blue-400" /> Subencargados</h2>
              <div className="rounded-2xl border border-slate-800 overflow-x-auto">
                <table className="w-full text-sm" data-testid="trust-subprocessors-table">
                  <thead className="bg-slate-900/60 text-slate-400 text-left"><tr><th className="p-3">Proveedor</th><th className="p-3">Finalidad</th><th className="p-3">Región</th></tr></thead>
                  <tbody>{(t.subprocessors || []).map((s) => <tr key={s.name} className="border-t border-slate-800"><td className="p-3 font-medium">{s.name}</td><td className="p-3 text-slate-400">{s.purpose}</td><td className="p-3 text-slate-400">{s.region}</td></tr>)}</tbody>
                </table>
              </div>
            </section>
            <section className="flex flex-wrap items-center gap-4 text-sm">
              <Link to="/seguridad" data-testid="trust-dpa-link" className="btn-primary px-4 py-2">Firmar el contrato de tratamiento de datos</Link>
              <span className="flex flex-wrap items-center gap-2" data-testid="trust-report-downloads">
                <span className="flex items-center gap-1.5 text-emerald-200"><Download className="w-4 h-4" /> Informe de cumplimiento (PDF):</span>
                {[['es', 'Español'], ['en', 'English'], ['pt', 'Português']].map(([l, n]) => (
                  <a key={l} href={`${API}/api/security/trust/report.pdf?lang=${l}`} data-testid={l === 'es' ? 'trust-report-download-btn' : `trust-report-download-${l}-btn`}
                    className="rounded-lg border border-emerald-500/50 px-3 py-1.5 text-emerald-200 hover:bg-emerald-500/10">{n}</a>
                ))}
              </span>
              {t.contact && <a href={`mailto:${t.contact}`} className="text-slate-400 hover:text-white flex items-center gap-2" data-testid="trust-contact-link"><Mail className="w-4 h-4" /> {t.contact}</a>}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
